import { cataloguePriceOrCurrent } from '../utils/invoiceLinePrice';
import InvoiceOrderDescription from '../components/InvoiceOrderDescription';
import InvoiceAmendments from '../components/InvoiceAmendments';
import { copyText } from '../utils/clipboard';
import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import InvoiceRefunds from '../components/InvoiceRefunds';
import { useAuth } from '../context/AuthContext';
const fmt = n => "\u20a6" + Number(n||0).toLocaleString("en-NG");

const Badge = ({ status }) => {
  const cls = status==="paid"?"b-paid":status==="part_paid"?"b-part":"b-unpaid";
  return <span className={"badge "+cls}>{(status||"").replace(/_/g," ")}</span>;
};

const CONFIRMATION_JOB_STATUSES = [
  "pending",
  "confirmed",
  "in_production",
  "printing",
  "quality_check",
  "ready",
  "dispatched"
];

const isoDate = value =>
  value ? String(value).slice(0, 10) : "";

const nextDate = value => {
  const normalized = isoDate(value);

  if (!normalized) return "";

  const date = new Date(
    normalized + "T12:00:00Z"
  );

  date.setUTCDate(
    date.getUTCDate() + 1
  );

  return date.toISOString().slice(0, 10);
};

const canOpenOrderConfirmation = invoice =>
  ["paid", "part_paid"].includes(
    invoice.status
  ) &&
  Number(invoice.active_job_count) === 1 &&
  CONFIRMATION_JOB_STATUSES.includes(
    invoice.job_order_status
  ) &&
  Boolean(
    isoDate(
      invoice.internal_production_due_date
    )
  ) &&
  Boolean(invoice.sales_rep_name) &&
  ![
    "sending",
    "delivery_unknown"
  ].includes(
    invoice.latest_confirmation_status
  );

const DEFAULT_TERMS = [
  "80% deposit required to validate and commence order.",
  "Production begins only after print design is confirmed and approved by client.",
  "Order will be ready within 10-14 working days after design approval.",
  "Balance payment (20%) must be received in full before order is dispatched or released for collection.",
  "This proforma invoice is valid for 7 days from the date of issue."
];


async function loadAllInvoiceCustomers() {
  const pageSize = 200;
  const records = [];
  let page = 1;

  while (page <= 1000) {
    const response = await get(
      `/customers?page=${page}&limit=${pageSize}`
    );

    const rows = response.data || [];
    records.push(...rows);

    const total = Number(response.total);

    if (
      Number.isFinite(total)
        ? records.length >= total
        : rows.length < pageSize
    ) {
      break;
    }

    page += 1;
  }

  const uniqueCustomers = Array.from(
    new Map(
      records.map(customer => [
        customer.id,
        customer
      ])
    ).values()
  );

  return uniqueCustomers.sort(
    (left, right) =>
      String(left.name || '').localeCompare(
        String(right.name || ''),
        undefined,
        { sensitivity: 'base' }
      )
  );
}

export default function Invoices() {
  const { hasPermission, user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [viewModal, setViewModal] = useState(null);
  const [printModal, setPrintModal] = useState(null);
  const [thermalModal, setThermalModal] = useState(null);
  const [pendingDiscounts, setPendingDiscounts] = useState([]);
  const [showPendingDiscounts, setShowPendingDiscounts] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customProducts, setCustomProducts] = useState([]);

  const load = () => {
    setLoading(true);
    const qs = filter ? "?status="+filter : "";
    get("/invoices"+qs).then(d => setInvoices(d.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    if (hasPermission('invoices:write')) {
      loadAllInvoiceCustomers()
      .then(setCustomers)
      .catch(error => {
        console.error(
          'Customer list failed:',
          error
        );
      });
      get("/products").then(d => setProducts(d.data));
      get("/custom-products").then(d => setCustomProducts(d.data));
    }
  }, [filter]);

  const refreshPendingDiscounts = async () => {
    if (user?.role !== 'super_admin') {
      setPendingDiscounts([]);
      return;
    }

    try {
      const response =
        await get('/invoices/pending-discounts');

      setPendingDiscounts(
        response.data || []
      );
    } catch (err) {
      console.error(
        'Pending discount refresh failed:',
        err
      );
    }
  };

  const handleApproveDiscount = async id => {
    const confirmed = window.confirm(
      'Approve this discount and issue the signed invoice?'
    );

    if (!confirmed) return null;

    try {
      const response = await put(
        '/invoices/' + id + '/approve-discount',
        {}
      );

      await Promise.all([
        refreshPendingDiscounts(),
        Promise.resolve(load())
      ]);

      window.alert(
        response.message ||
        'Discount approved successfully.'
      );

      return response;
    } catch (err) {
      window.alert(
        err.message ||
        'The discount could not be approved.'
      );

      return null;
    }
  };

  const handleRejectDiscount = async id => {
    const reason = window.prompt(
      'Why are you rejecting this discount?'
    );

    if (reason === null) return null;

    if (!reason.trim()) {
      window.alert(
        'A rejection reason is required.'
      );
      return null;
    }

    const confirmed = window.confirm(
      'Reject this discount and restore the invoice to its full price?'
    );

    if (!confirmed) return null;

    try {
      const response = await put(
        '/invoices/' + id + '/reject-discount',
        {
          rejection_reason:
            reason.trim()
        }
      );

      await Promise.all([
        refreshPendingDiscounts(),
        Promise.resolve(load())
      ]);

      window.alert(
        response.message ||
        'Discount rejected successfully.'
      );

      return response;
    } catch (err) {
      window.alert(
        err.message ||
        'The discount could not be rejected.'
      );

      return null;
    }
  };

  const filtered = invoices.filter(i => {
    if (search && !i.invoice_number.toLowerCase().includes(search.toLowerCase()) &&
        !i.customer_name.toLowerCase().includes(search.toLowerCase())) return false;
    if (period !== 'all' && period !== 'custom') {
      const d = new Date(i.date);
      const now = new Date();
      if (period === 'today') {
        if (i.date !== now.toISOString().split('T')[0]) return false;
      } else if (period === 'month') {
        if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return false;
      } else if (period === 'last_month') {
        const lm = new Date(now.getFullYear(), now.getMonth()-1, 1);
        if (d.getMonth() !== lm.getMonth() || d.getFullYear() !== lm.getFullYear()) return false;
      } else if (period === 'week') {
        const weekAgo = new Date(now - 7*86400000);
        if (d < weekAgo) return false;
      }
    }
    if (period === 'custom') {
      if (dateFrom && i.date < dateFrom) return false;
      if (dateTo && i.date > dateTo) return false;
    }
    return true;
  });

  return (
    <div>
      <div className="flex-between mb-lg">
        <h2 className="page-title">Invoices</h2>
        {hasPermission("invoices:write") && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ New Invoice</button>
        )}
      </div>
      <InvoiceAmendments onChanged={load} />
      <div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap',alignItems:'center'}}>
        {[['all','All Time'],['today','Today'],['week','This Week'],['month','This Month'],['last_month','Last Month'],['custom','Custom']].map(([key,label])=>(
          <button key={key} className={'btn btn-sm '+(period===key?'btn-primary':'btn-outline')}
            onClick={()=>setPeriod(key)}>{label}</button>
        ))}
        {period==='custom' && (
          <>
            <input type="date" className="form-control" style={{maxWidth:150,fontSize:12}}
              value={dateFrom} onChange={e=>setDateFrom(e.target.value)} />
            <span style={{fontSize:12,color:'#8A8A8A'}}>to</span>
            <input type="date" className="form-control" style={{maxWidth:150,fontSize:12}}
              value={dateTo} onChange={e=>setDateTo(e.target.value)} />
          </>
        )}
      </div>
      <div className="search-bar">
        <input placeholder="Search invoices..." value={search} onChange={e => setSearch(e.target.value)} />
        <select value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="unpaid">Unpaid</option>
          <option value="part_paid">Part Paid</option>
          <option value="paid">Paid</option>
        </select>
      </div>
      {/* Pending discount approvals */}
      {user?.role === 'super_admin' && pendingDiscounts.length > 0 && (
        <div style={{background:'#FFF3DC',border:'1px solid #F5C842',borderRadius:8,padding:'12px 20px',marginBottom:16}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:showPendingDiscounts?12:0}}>
            <div style={{fontSize:13,fontWeight:500,color:'#8B5E0A'}}>
              🏷 <strong>{pendingDiscounts.length}</strong> invoice discount{pendingDiscounts.length!==1?'s':''} awaiting your approval
            </div>
            <button className="btn btn-sm" style={{background:'#8B5E0A',color:'#fff',border:'none'}}
              onClick={()=>setShowPendingDiscounts(!showPendingDiscounts)}>
              {showPendingDiscounts?'Hide':'Review'}
            </button>
          </div>
          {showPendingDiscounts && (
            <div style={{background:'#fff',borderRadius:8,overflow:'hidden'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'#8B5E0A'}}>
                  {['Invoice','Customer','Subtotal','Discount','Rep',''].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#fff',textTransform:'uppercase'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {pendingDiscounts.map((inv,i) => (
                    <tr key={inv.id} style={{background:i%2===0?'#fff':'#FFFBF0'}}>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600,color:'#556B2F'}}>{inv.invoice_number}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{inv.customer_name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif'}}>₦{Number(inv.subtotal).toLocaleString()}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600,color:'#8B5E0A'}}>
                        {inv.discount_type==='percentage' ? inv.discount_value+'%' : '₦'+Number(inv.discount_value).toLocaleString()}
                      </td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{inv.created_by_name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        <div style={{display:'flex',gap:6}}>
                          <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none',fontSize:11}}
                            onClick={()=>handleApproveDiscount(inv.id)}>✓ Approve</button>
                          <button className="btn btn-danger btn-sm" style={{fontSize:11}}
                            onClick={()=>handleRejectDiscount(inv.id)}>✗ Reject</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="tbl-wrap">
        {loading ? <div className="spinner"></div> : (
          <table>
            <thead><tr>
              <th>Invoice #</th><th>Date</th><th>Customer</th>
              <th style={{textAlign:"right"}}>Total</th>
              <th style={{textAlign:"right"}}>Paid</th>
              <th style={{textAlign:"right"}}>Balance</th>
              <th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {filtered.length === 0
                ? <tr><td colSpan="8"><div className="empty-state"><h3>No invoices yet</h3></div></td></tr>
                : filtered.map(inv => (
                  <tr key={inv.id}>
                    <td style={{fontWeight:500}}>{inv.invoice_number}</td>
                    <td>{inv.date}</td>
                    <td>{inv.customer_name}</td>
                    <td className="amt">{fmt(inv.total)}</td>
                    <td className="amt">{fmt(inv.amount_paid)}</td>
                    <td className="amt" style={{color:inv.balance_due>0?"var(--unpaid)":"var(--paid)"}}>
                      {fmt(inv.balance_due)}
                    </td>
                    <td><Badge status={inv.status} /></td>
                    <td>
                      <div className="flex-center gap-sm">
                        <button className="btn btn-ghost btn-sm" onClick={() => setViewModal(inv)}>View</button>
                        {hasPermission("invoices:send_confirmation") &&
                         canOpenOrderConfirmation(inv) && (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() =>
                              setConfirmationModal(inv)
                            }
                          >
                            Order Confirmation
                          </button>
                        )}
                        {hasPermission("invoices:write") && inv.status !== "cancelled" && (
                          <button className="btn btn-ghost btn-sm" onClick={() => setEditModal(inv)}>Edit</button>
                        )}
                        {hasPermission("payments:write") && inv.status !== "paid" && inv.status !== "cancelled" && (
                          <button className="btn btn-gold btn-sm" onClick={() => setPayModal(inv)}>Pay</button>
                        )}
                        {inv.discount_status==='pending' && (
                          <span style={{fontSize:9,fontWeight:700,background:'#FFF3DC',color:'#8B5E0A',padding:'2px 8px',borderRadius:20}}>DISCOUNT PENDING</span>
                        )}
                        {inv.discount_status !== 'pending' && (
                          <>
                            <button className="btn btn-outline btn-sm" onClick={() => setPrintModal(inv)}>PDF</button>
                            <button className="btn btn-outline btn-sm" style={{fontSize:10}} onClick={() => setThermalModal(inv)}>🖨️ Receipt</button>
                          </>
                        )}
                        {hasPermission("invoices:delete") && (
                          <button className="btn btn-danger btn-sm" onClick={() => setDeleteModal(inv)}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        )}
      </div>
      {showCreate && (
        <InvoiceFormModal
          customers={customers} products={products} customProducts={customProducts} invoice={null}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }} />
      )}
      {editModal && (
        <InvoiceFormModal
          customers={customers} products={products} customProducts={customProducts} invoice={editModal}
          onClose={() => setEditModal(null)}
          onSaved={() => { setEditModal(null); load(); }} />
      )}
      {payModal && (
        <PaymentModal invoice={payModal}
          onClose={() => setPayModal(null)}
          onSaved={() => { setPayModal(null); load(); }} />
      )}
      {viewModal && (
        <ViewInvoiceModal
          invoice={viewModal}
          onRefundUpdated={load}
          onClose={() => setViewModal(null)}
          onPay={inv => {
            setViewModal(null);
            setPayModal(inv);
          }}
          onPrint={inv => {
            setViewModal(null);
            setPrintModal(inv);
          }}
          onApproveDiscount={
            handleApproveDiscount
          }
          onRejectDiscount={
            handleRejectDiscount
          }
        />
      )}
      {confirmationModal && (
        <OrderConfirmationModal
          invoice={confirmationModal}
          onClose={() =>
            setConfirmationModal(null)
          }
          onSent={() => {
            setConfirmationModal(null);
            load();
          }}
        />
      )}
      {deleteModal && (
        <DeleteInvoiceModal invoice={deleteModal}
          onClose={() => setDeleteModal(null)}
          onDeleted={() => { setDeleteModal(null); load(); }} />
      )}
      {thermalModal && (
        <ThermalReceiptModal invoice={thermalModal} onClose={() => setThermalModal(null)} />
      )}
      {printModal && (
        <PrintInvoiceModal invoice={printModal}
          onClose={() => setPrintModal(null)} />
      )}
    </div>
  );
}

function OrderConfirmationModal({
  invoice,
  onClose,
  onSent
}) {
  const internalDate = isoDate(
    invoice.internal_production_due_date
  );

  const minimumDate = nextDate(
    internalDate
  );

  const previouslySent = Boolean(
    invoice.last_sent_at
  );

  const hasEmail = Boolean(
    invoice.resolved_customer_email
  );

  const [customerReadyDate, setCustomerReadyDate] =
    React.useState(
      isoDate(invoice.customer_ready_date) ||
        minimumDate
    );

  const [preview, setPreview] =
    React.useState(null);

  const [previewing, setPreviewing] =
    React.useState(false);

  const [sending, setSending] =
    React.useState(false);

  const [creatingLink, setCreatingLink] =
    React.useState(false);

  const [copying, setCopying] =
    React.useState(false);

  const [shareLink, setShareLink] =
    React.useState(null);

  const [error, setError] =
    React.useState("");

  const [notice, setNotice] =
    React.useState("");

  const generatePreview = async () => {
    if (
      !customerReadyDate ||
      customerReadyDate < minimumDate
    ) {
      setError(
        "Customer-ready date must be later than the internal production deadline."
      );
      return;
    }

    setPreviewing(true);
    setPreview(null);
    setShareLink(null);
    setError("");
    setNotice("");

    try {
      const response = await post(
        "/invoices/" +
          invoice.id +
          "/order-confirmation-preview",
        {
          customer_ready_date:
            customerReadyDate
        }
      );

      setPreview(response.data);
    } catch (err) {
      setError(
        err.message ||
          "The confirmation preview could not be generated."
      );
    } finally {
      setPreviewing(false);
    }
  };

  const sendConfirmation = async () => {
    if (!hasEmail) {
      setError(
        "Add a valid customer email address before sending by email."
      );
      return;
    }

    if (!preview) {
      setError(
        "Generate and review the confirmation before sending."
      );
      return;
    }

    const action = previouslySent
      ? "resend"
      : "send";

    const confirmed = window.confirm(
      `Are you sure you want to ${action} ` +
      `this confirmation to ` +
      `${preview.recipient_email}?`
    );

    if (!confirmed) return;

    setSending(true);
    setError("");
    setNotice("");

    try {
      await post(
        "/invoices/" +
          invoice.id +
          "/order-confirmation-send",
        {
          customer_ready_date:
            customerReadyDate,
          resend:
            previouslySent,
          confirm_resend:
            previouslySent
        }
      );

      window.alert(
        previouslySent
          ? "Order confirmation resent successfully."
          : "Order confirmation sent successfully."
      );

      onSent();
    } catch (err) {
      setError(
        err.message ||
          "The order confirmation could not be sent."
      );
    } finally {
      setSending(false);
    }
  };

  const recordCopy = async link => {
    try {
      await post(
        "/invoices/" +
          invoice.id +
          "/order-confirmation-links/" +
          link.id +
          "/copied",
        {}
      );

      return true;
    } catch (err) {
      return false;
    }
  };

  const copyShareMessage = async link => {
    setCopying(true);
    setError("");
    setNotice("");

    try {
      await copyText(
        link.share_message
      );
    } catch (err) {
      setError(
        "The secure link exists, but automatic copying failed. Select and copy the complete message shown below."
      );
      setCopying(false);
      return;
    }

    const auditRecorded =
      await recordCopy(link);

    if (auditRecorded) {
      setNotice(
        "The complete customer message and secure link were copied successfully."
      );
    } else {
      setError(
        "The message was copied, but BloansBooks could not record the copy event."
      );
    }

    setCopying(false);
  };

  const createAndCopyMessage = async () => {
    if (!preview) {
      setError(
        "Generate and review the confirmation before creating its secure link."
      );
      return;
    }

    const confirmed = window.confirm(
      "Creating this link starts the customer’s 12-hour review period immediately. If an active link already exists, its old secret will stop working, but the original deadline will not restart. Continue?"
    );

    if (!confirmed) return;

    setCreatingLink(true);
    setError("");
    setNotice("");

    try {
      const response = await post(
        "/invoices/" +
          invoice.id +
          "/order-confirmation-link",
        {
          customer_ready_date:
            customerReadyDate
        }
      );

      const link = response.data;

      setShareLink(link);

      await copyShareMessage(link);
    } catch (err) {
      setError(
        err.message ||
          "The secure confirmation message could not be created."
      );
    } finally {
      setCreatingLink(false);
    }
  };

  const changeReadyDate = event => {
    setCustomerReadyDate(
      event.target.value
    );

    setPreview(null);
    setShareLink(null);
    setError("");
    setNotice("");
  };

  const formatDeadline = value =>
    new Intl.DateTimeFormat(
      "en-NG",
      {
        timeZone: "Africa/Lagos",
        dateStyle: "long",
        timeStyle: "short"
      }
    ).format(
      new Date(value)
    );

  return (
    <div className="modal-overlay">
      <div
        className="modal"
        style={{
          maxWidth: 920,
          width: "95vw"
        }}
      >
        <div className="modal-hdr">
          <span className="modal-title">
            Order Confirmation
          </span>

          <button
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            disabled={
              sending ||
              creatingLink ||
              copying
            }
          >
            X
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-error mb-md">
              {error}
            </div>
          )}

          {notice && (
            <div
              className="alert mb-md"
              style={{
                background: "#EFF7F0",
                border: "1px solid #B8D4BD",
                color: "#2E6B3A"
              }}
            >
              {notice}
            </div>
          )}

          {previouslySent && (
            <div
              className="alert mb-md"
              style={{
                background: "#FFF3DC",
                border: "1px solid #F5C842",
                color: "#8B5E0A"
              }}
            >
              An email confirmation was previously
              sent. Sending another email will be
              recorded as a resend.
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit,minmax(200px,1fr))",
              gap: 12,
              marginBottom: 16
            }}
          >
            <div>
              <div className="form-label">
                Invoice
              </div>
              <strong>
                {invoice.invoice_number}
              </strong>
            </div>

            <div>
              <div className="form-label">
                Customer
              </div>
              <strong>
                {invoice.customer_name}
              </strong>
            </div>

            <div>
              <div className="form-label">
                Email Recipient
              </div>
              <strong>
                {hasEmail
                  ? invoice.resolved_customer_email
                  : "No email provided"}
              </strong>
            </div>

            <div>
              <div className="form-label">
                Representative
              </div>
              <strong>
                {invoice.sales_rep_name}
              </strong>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit,minmax(220px,1fr))",
              gap: 12,
              marginBottom: 16
            }}
          >
            <div>
              <label className="form-label">
                Internal Production Deadline
              </label>
              <input
                type="date"
                className="form-control"
                value={internalDate}
                disabled
              />
            </div>

            <div>
              <label className="form-label">
                Customer Promised Ready Date *
              </label>
              <input
                type="date"
                className="form-control"
                value={customerReadyDate}
                min={minimumDate}
                onChange={changeReadyDate}
                disabled={
                  sending ||
                  creatingLink ||
                  Boolean(shareLink)
                }
              />

              <div
                style={{
                  color: "#8A8A8A",
                  fontSize: 11,
                  marginTop: 5
                }}
              >
                Earliest permitted date:{" "}
                {minimumDate}
              </div>
            </div>
          </div>

          <div
            style={{
              padding: 12,
              border: "1px solid #EDE7D8",
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 12
            }}
          >
            <div>
              <strong>Email sender:</strong>{" "}
              noreply@bloanshandbags.com
            </div>

            <div>
              <strong>Email replies:</strong>{" "}
              support@bloanshandbags.com
            </div>

            {!hasEmail && (
              <div
                style={{
                  marginTop: 6,
                  color: "#8B5E0A"
                }}
              >
                Email delivery is unavailable.
                You can still create and copy a
                secure confirmation message.
              </div>
            )}
          </div>

          {!preview ? (
            <div
              style={{
                padding: 28,
                textAlign: "center",
                background: "#F8F6F1",
                borderRadius: 8,
                color: "#6F675F"
              }}
            >
              Choose the customer-ready date,
              then generate the confirmation preview.
            </div>
          ) : (
            <>
              <div
                style={{
                  fontSize: 13,
                  marginBottom: 8
                }}
              >
                <strong>Subject:</strong>{" "}
                {preview.subject}
              </div>

              <iframe
                title="Order confirmation preview"
                srcDoc={preview.html}
                sandbox=""
                referrerPolicy="no-referrer"
                style={{
                  width: "100%",
                  height: 520,
                  border:
                    "1px solid #D8D1C4",
                  borderRadius: 8,
                  background: "#fff"
                }}
              />
            </>
          )}

          {shareLink && (
            <div
              style={{
                marginTop: 16,
                padding: 14,
                border: "1px solid #C8D8A8",
                borderRadius: 8,
                background: "#F7FAF1"
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  marginBottom: 8
                }}
              >
                Customer Message
              </div>

              <textarea
                className="form-control"
                readOnly
                value={shareLink.share_message}
                rows={14}
                style={{
                  fontSize: 12,
                  lineHeight: 1.5,
                  resize: "vertical"
                }}
              />

              <div
                style={{
                  marginTop: 8,
                  fontSize: 11,
                  color: "#70685F"
                }}
              >
                Review deadline:{" "}
                {formatDeadline(
                  shareLink.review_expires_at
                )}
              </div>

              <button
                type="button"
                className="btn btn-outline btn-sm"
                style={{marginTop: 10}}
                onClick={() =>
                  copyShareMessage(shareLink)
                }
                disabled={
                  copying ||
                  creatingLink
                }
              >
                {copying
                  ? "Copying..."
                  : "Copy Message Again"}
              </button>
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: 8,
            padding: "14px 20px",
            borderTop: "1px solid #EDE7D8"
          }}
        >
          <button
            className="btn btn-ghost"
            onClick={onClose}
            disabled={
              sending ||
              creatingLink ||
              copying
            }
          >
            Cancel
          </button>

          <button
            className="btn btn-outline"
            onClick={generatePreview}
            disabled={
              previewing ||
              sending ||
              creatingLink ||
              Boolean(shareLink) ||
              !customerReadyDate
            }
          >
            {previewing
              ? "Generating..."
              : preview
                ? "Refresh Preview"
                : "Generate Preview"}
          </button>

          <button
            className="btn btn-gold"
            onClick={createAndCopyMessage}
            disabled={
              !preview ||
              previewing ||
              sending ||
              creatingLink ||
              copying
            }
          >
            {creatingLink
              ? "Creating..."
              : shareLink
                ? "Replace and Copy Message"
                : "Create and Copy Confirmation Message"}
          </button>

          <button
            className={
              previouslySent
                ? "btn btn-danger"
                : "btn btn-primary"
            }
            onClick={sendConfirmation}
            disabled={
              !hasEmail ||
              !preview ||
              previewing ||
              sending ||
              creatingLink
            }
            title={
              hasEmail
                ? ""
                : "Add a customer email address to send by email"
            }
          >
            {!hasEmail
              ? "Email Unavailable"
              : sending
                ? "Sending..."
                : previouslySent
                  ? "Confirm Email Resend"
                  : "Confirm and Send Email"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── INVOICE FORM (Create + Edit) ─────────────────────────
function InvoiceFormModal({ customers, products, customProducts, invoice, onClose, onSaved }) {
  const isEdit = !!invoice?.id;
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [confirmZeroPrices,setConfirmZeroPrices] = React.useState(false);
  const [fullInv, setFullInv] = React.useState(null);
  const hasRecordedPayment=Number(fullInv?.amount_paid||0)>0 || (fullInv?.payments||[]).some(payment=>Number(payment.amount)>0);
  const needsEditApproval=isEdit && !isSuperAdmin && hasRecordedPayment;
  const [form, setForm] = React.useState({
    customer_id: invoice?.customer_id || "",
    date: invoice?.date || new Date().toISOString().split("T")[0],
    due_date: invoice?.due_date || "",
    discount_type: invoice?.discount_type || "fixed",
    invoice_type: invoice?.invoice_type || "invoice",
    discount_value: invoice?.discount_value || 0,
    notes: invoice?.notes || ""
  });
  const [lines, setLines] = React.useState([
    { product_id:"", product_name:"", description:"", quantity:1, unit_price:0 }
  ]);
  const [amendmentReason,setAmendmentReason]=React.useState('');
  const [customerApproval,setCustomerApproval]=React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [
    customerSearch,
    setCustomerSearch
  ] = React.useState('');

  const visibleCustomers =
    customers.filter(customer => {
      const term =
        customerSearch
          .trim()
          .toLowerCase();

      if (!term) return true;

      return [
        customer.name,
        customer.customer_code,
        customer.phone,
        customer.email
      ].some(value =>
        String(value || '')
          .toLowerCase()
          .includes(term)
      );
    });

  React.useEffect(() => {
    if (isEdit && invoice?.id) {
      get("/invoices/" + invoice.id).then(data => {
        setFullInv(data);
        setForm({
          customer_id: data.customer_id || "",
          date: isoDate(data.date),
          due_date: isoDate(data.due_date),
          discount_type: data.discount_type || "fixed",
          discount_value: data.discount_value || 0,
          notes: data.notes || ""
        });
        if (data.lines && data.lines.length > 0) {
          setLines(data.lines.map(l => ({
            product_id: l.product_id || "",
            product_name: l.product_name || "",
            description: l.description || "",
            size: l.size || "",
            quantity: l.quantity || 1,
            unit_price: l.unit_price || 0
          })));
        }
      });
    }
  }, [invoice?.id]);

  const sub = lines.reduce((s,l) => s + (parseFloat(l.quantity||0) * parseFloat(l.unit_price||0)), 0);
  const disc = form.discount_type === "percentage"
    ? (sub * parseFloat(form.discount_value||0) / 100)
    : parseFloat(form.discount_value||0);
  const effectiveDisc=isSuperAdmin ? disc : 0;
  const refundReduction=isEdit ? Number(fullInv?.refund_reduction_total||0) : 0;
  const total = Math.max(0, sub - effectiveDisc - refundReduction);

  const updateLine = (i, field, val) => {
    const updated = [...lines];
    setConfirmZeroPrices(false);
    updated[i] = { ...updated[i], [field]: val };
    if (field === "product_id" && val) {
      if (val.startsWith("cp-")) {
        const cpId = val.replace("cp-", "");
        const cp = (customProducts||[]).find(x => x.id === cpId);
        if (cp) {
          updated[i].product_name = cp.name;
          updated[i].unit_price=cataloguePriceOrCurrent(cp.default_price,updated[i].unit_price);
        }
      } else {
        const p = products.find(x => x.id === val);
        if (p) {
          updated[i].product_name = p.name + (p.size ? " ("+p.size+")" : "");
          updated[i].unit_price=cataloguePriceOrCurrent(p.selling_price,updated[i].unit_price);
        }
      }
    }
    setLines(updated);
  };

  const submit = async () => {
    if (!form.customer_id) { setError("Select a customer"); return; }
    if(isEdit&&amendmentReason.trim().length<10){setError('Explain the amendment in at least 10 characters');return;}
    const validLines = lines.filter(l => l.description || l.product_name);
    if (!validLines.length) { setError("Add at least one item"); return; }
    if(validLines.some(l => String(l.unit_price ?? '').trim()==='' || !Number.isFinite(Number(l.unit_price)) || Number(l.unit_price)<0 || !Number.isFinite(Number(l.quantity)) || Number(l.quantity)<=0)) {
      setError('Enter a valid quantity and price for every item'); return;
    }
    if(validLines.some(l => Number(l.unit_price)===0)&&!confirmZeroPrices) {
      setError('Enter the missing prices or explicitly confirm complimentary items'); return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        confirm_zero_prices:confirmZeroPrices,
        amendment_reason:amendmentReason,
        customer_approval_reference:customerApproval,
        lines: validLines.map(l => ({
          product_id: l.product_id && !l.product_id.startsWith("cp-") ? l.product_id : null,
          size: l.size || null,
          description: !isEdit && l.product_name
            ? (l.product_name + (l.description ? " - " + l.description : ""))
            : l.description,
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price)
        }))
      };
      if (isEdit) {
        const result=await put("/invoices/"+invoice.id, payload);
        window.alert(result.direct_edit ? 'Invoice changes saved.' : 'Amendment submitted to Super Admin. The current invoice remains unchanged until approval.');
      } else {
        await post("/invoices", payload);
      }
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  if (isEdit && !fullInv) return (
    <div className="modal-overlay">
      <div className="modal" style={{textAlign:"center",padding:40}}>
        <div className="spinner"></div>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:700}}>
        <div className="modal-hdr">
          <span className="modal-title">{isEdit ? (needsEditApproval ? "Request Invoice Amendment" : "Edit Invoice") : "New Invoice"}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          {lines.some(l => Number(l.unit_price)===0 && (l.description || l.product_name)) && <div style={{background:'#FFF3DC',padding:12,marginBottom:16}}>
            <label><input type="checkbox" checked={confirmZeroPrices} onChange={e=>setConfirmZeroPrices(e.target.checked)} /> I confirm that every zero-priced item on this invoice is complimentary.</label>
          </div>}
          {isEdit&&<div style={{background:'#F2EFE7',padding:14,marginBottom:16}}>
            <p>{needsEditApproval ? "This invoice has a recorded payment and requires Super Admin approval. Specification amendments hold production while pending." : "Your edits save immediately with an audit trail. Specification changes require an updated production review."}</p>
            <label className="form-label">Amendment reason *</label>
            <textarea className="form-control" value={amendmentReason} onChange={e=>setAmendmentReason(e.target.value)} />
            <label className="form-label">Customer approval reference (required for specification changes)</label>
            <input className="form-control" value={customerApproval} onChange={e=>setCustomerApproval(e.target.value)} placeholder="Customer approval date and WhatsApp/email reference" />
          </div>}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Customer *</label>
              <input
              className="form-control"
              value={customerSearch}
              onChange={event =>
                setCustomerSearch(
                  event.target.value
                )
              }
              placeholder="Search name, phone, email or customer code"
              style={{ marginBottom: 8 }}
            />
            <select className="form-control" value={form.customer_id}
                onChange={e => setForm({...form, customer_id:e.target.value})}>
                <option value="">Select customer</option>
                {visibleCustomers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Invoice Date *</label>
              <div className="form-group" style={{marginBottom:12}}>
              <label className="form-label">Document Type</label>
              <select className="form-control" value={form.invoice_type}
                onChange={e=>setForm({...form,invoice_type:e.target.value})}>
                <option value="invoice">Invoice</option>
                <option value="proforma">Proforma Invoice</option>
              </select>
            </div>
            <input type="date" className="form-control" value={form.date}
                onChange={e => setForm({...form, date:e.target.value})} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Due Date</label>
            <input type="date" className="form-control" value={form.due_date||""}
              onChange={e => setForm({...form, due_date:e.target.value})} />
          </div>

          <div className="divider"></div>
          <div className="flex-between mb-md">
            <span style={{fontWeight:500,fontSize:13}}>Line Items</span>
            <button className="btn btn-outline btn-sm"
              onClick={() => setLines([...lines, {product_id:"",product_name:"",description:"",quantity:1,unit_price:0}])}>
              + Add Item
            </button>
          </div>

          {/* Column headers */}
          <div style={{display:"grid",gridTemplateColumns:"1.8fr 2fr 0.8fr 1.2fr auto",gap:6,marginBottom:4}}>
            <div style={{fontSize:10,fontWeight:600,color:"#8A8A8A",textTransform:"uppercase",letterSpacing:".08em",padding:"0 2px"}}>Product</div>
            <div style={{fontSize:10,fontWeight:600,color:"#8A8A8A",textTransform:"uppercase",letterSpacing:".08em",padding:"0 2px"}}>Description / Notes</div>
            <div style={{fontSize:10,fontWeight:600,color:"#8A8A8A",textTransform:"uppercase",letterSpacing:".08em",padding:"0 2px"}}>Qty</div>
            <div style={{fontSize:10,fontWeight:600,color:"#8A8A8A",textTransform:"uppercase",letterSpacing:".08em",padding:"0 2px"}}>Unit Price</div>
            <div></div>
          </div>

          {lines.map((line,i) => (
            <div key={i} style={{display:"grid",gridTemplateColumns:"1.8fr 2fr 0.8fr 1.2fr auto",gap:6,marginBottom:8,alignItems:"center"}}>
              <select className="form-control" style={{fontSize:12}} value={line.product_id}
                onChange={e => updateLine(i, "product_id", e.target.value)}>
                <option value="">-- Select or type below --</option>
                {(customProducts||[]).length > 0 && (
                  <optgroup label="Custom Products (Made to Order)">
                    {(customProducts||[]).map(p => (
                      <option key={"cp-"+p.id} value={"cp-"+p.id}>{p.name}</option>
                    ))}
                  </optgroup>
                )}
                {products.length > 0 && (
                  <optgroup label="Finished Goods (In Stock)">
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.name}{p.size?" ("+p.size+")":""}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              <InvoiceOrderDescription
                value={line.description}
                onChange={value => updateLine(i, "description", value)}
              />
              <input type="number" className="form-control" style={{fontSize:12}}
                placeholder="Qty" value={line.quantity}
                onChange={e => updateLine(i, "quantity", e.target.value)} />
              <input type="number" className="form-control" style={{fontSize:12}}
                placeholder="Price" value={line.unit_price}
                onChange={e => updateLine(i, "unit_price", e.target.value)} />
              <button className="btn btn-ghost btn-sm"
                onClick={() => setLines(lines.filter((_,idx) => idx !== i))}
                disabled={lines.length===1} style={{padding:8,color:"var(--ink-light)"}}>X</button>
            </div>
          ))}

          <div className="divider"></div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            <div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Discount Type</label>
                  <select className="form-control" value={form.discount_type}
                    onChange={e => setForm({...form, discount_type:e.target.value})}>
                    <option value="fixed">Fixed (N)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Discount Value</label>
                  <input type="number" className="form-control" value={form.discount_value}
                    onChange={e => setForm({...form, discount_value:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={form.notes}
                  onChange={e => setForm({...form, notes:e.target.value})} />
              </div>
            </div>
            <div style={{background:"#F2EFE7",borderRadius:8,padding:16}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:8,fontSize:13}}>
                <span style={{color:"#8A8A8A"}}>Subtotal</span><span>{fmt(sub)}</span></div>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:8,fontSize:13}}>
                <span style={{color:"#8A8A8A"}}>Discount</span>
                <span style={{color:"#8F2E24"}}>-{fmt(effectiveDisc)}</span></div>
              {refundReduction>0 && <div style={{display:"flex",justifyContent:"space-between",marginBottom:8,fontSize:13}}><span>Approved refund reductions</span><span>-{fmt(refundReduction)}</span></div>}
              <div style={{display:"flex",justifyContent:"space-between",paddingTop:8,borderTop:"1px solid #DDD4C0"}}>
                <span style={{fontWeight:600}}>Total</span>
                <span style={{fontFamily:"Cormorant Garamond,serif",fontSize:"1.3rem",fontWeight:600,color:"#556B2F"}}>{fmt(total)}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? "Saving..." : isEdit ? (needsEditApproval ? "Request Approval" : "Save Changes") : "Create Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── VIEW INVOICE ─────────────────────────────────────────
function ViewInvoiceModal({
  invoice,
  onClose,
  onPay,
  onPrint,
  onApproveDiscount,
  onRejectDiscount,
  onRefundUpdated
}) {
  const [full, setFull] = React.useState(null);
  const [decisionBusy, setDecisionBusy] =
    React.useState(false);
  const [decisionError, setDecisionError] =
    React.useState('');
  const [paymentUrl, setPaymentUrl] =
    React.useState('');
  const [paymentLinkError, setPaymentLinkError] =
    React.useState('');
  const [copyingPaymentLink, setCopyingPaymentLink] =
    React.useState(false);
  const { hasPermission, user } = useAuth();

  React.useEffect(() => {
    get("/invoices/" + invoice.id).then(setFull);
  }, [invoice.id]);

  const refreshInvoice = async () => {
    const refreshed =
      await get('/invoices/' + invoice.id);

    setFull(refreshed);
    return refreshed;
  };

  React.useEffect(() => {
    const eligible =
      full &&
      Number(full.balance_due || 0) > 0 &&
      full.status !== 'paid' &&
      full.status !== 'cancelled' &&
      full.discount_status !== 'pending';

    if (!eligible) {
      setPaymentUrl('');
      setPaymentLinkError('');
      return;
    }

    let cancelled = false;

    post(
      '/invoice-payment-links/invoice/' +
        full.id,
      {}
    )
      .then(response => {
        if (cancelled) return;

        setPaymentUrl(
          response?.data?.payment_url || ''
        );
        setPaymentLinkError('');
      })
      .catch(error => {
        if (cancelled) return;

        const message =
          error?.message || '';

        if (
          /not currently available|not configured|disabled/i
            .test(message)
        ) {
          setPaymentUrl('');
          setPaymentLinkError('');
          return;
        }

        setPaymentUrl('');
        setPaymentLinkError(
          message ||
          'The payment link could not be created.'
        );
      });

    return () => {
      cancelled = true;
    };
  }, [
    full?.id,
    full?.balance_due,
    full?.status,
    full?.discount_status
  ]);

  const copyPaymentLink = async () => {
    if (!paymentUrl || copyingPaymentLink) {
      return;
    }

    setCopyingPaymentLink(true);

    try {
      await copyText(
        paymentUrl
      );

      window.alert('Payment link copied.');
    } catch (error) {
      window.prompt(
        'Copy this payment link:',
        paymentUrl
      );
    } finally {
      setCopyingPaymentLink(false);
    }
  };

  const approveDiscount = async () => {
    if (
      decisionBusy ||
      full?.discount_status !== 'pending'
    ) {
      return;
    }

    setDecisionBusy(true);
    setDecisionError('');

    try {
      const response =
        await onApproveDiscount(full.id);

      if (response) {
        await refreshInvoice();
      }
    } catch (err) {
      setDecisionError(
        err.message ||
        'The discount decision could not be completed.'
      );
    } finally {
      setDecisionBusy(false);
    }
  };

  const rejectDiscount = async () => {
    if (
      decisionBusy ||
      full?.discount_status !== 'pending'
    ) {
      return;
    }

    setDecisionBusy(true);
    setDecisionError('');

    try {
      const response =
        await onRejectDiscount(full.id);

      if (response) {
        await refreshInvoice();
      }
    } catch (err) {
      setDecisionError(
        err.message ||
        'The discount decision could not be completed.'
      );
    } finally {
      setDecisionBusy(false);
    }
  };

  if (!full) return (
    <div className="modal-overlay">
      <div className="modal" style={{textAlign:"center",padding:40}}>
        <div className="spinner"></div>
      </div>
    </div>
  );

  const statusColor = full.status==="paid"?"var(--paid)":full.status==="part_paid"?"var(--part)":"var(--unpaid)";
  const statusBg    = full.status==="paid"?"var(--paid-bg)":full.status==="part_paid"?"var(--part-bg)":"var(--unpaid-bg)";

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:680}}>
        <div className="modal-hdr">
          <span className="modal-title">Invoice {full.invoice_number}</span>
          <div className="flex-center gap-sm">
            {hasPermission("payments:write") && full.status!=="paid" && full.status!=="cancelled" && (
              <button className="btn btn-gold btn-sm" onClick={() => onPay(full)}>Record Payment</button>
            )}
            {full.discount_status === 'pending' &&
             user?.role === 'super_admin' && (
              <>
                <button
                  className="btn btn-sm"
                  style={{
                    background:'#2E6B3A',
                    color:'#fff',
                    border:'none'
                  }}
                  disabled={decisionBusy}
                  onClick={approveDiscount}
                >
                  {decisionBusy
                    ? 'Working...'
                    : '✓ Approve Discount'}
                </button>

                <button
                  className="btn btn-danger btn-sm"
                  disabled={decisionBusy}
                  onClick={rejectDiscount}
                >
                  ✗ Reject Discount
                </button>
              </>
            )}

            {full.discount_status === 'pending' &&
             user?.role !== 'super_admin' && (
              <span style={{
                fontSize:10,
                fontWeight:700,
                color:'#8B5E0A',
                background:'#FFF3DC',
                padding:'6px 10px',
                borderRadius:20
              }}>
                DISCOUNT AWAITING APPROVAL
              </span>
            )}

            {full.discount_status !== 'pending' && (
              <button
                className="btn btn-outline btn-sm"
                onClick={() => onPrint(full)}
              >
                PDF
              </button>
            )}
            <button className="btn btn-outline btn-sm" style={{fontSize:11}} onClick={() => { onClose(); setThermalModal(full||invoice); }}>🖨️ Receipt</button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
          </div>
        </div>
        <div className="modal-body">
          {decisionError && (
            <div style={{
              background:'#FDEAEA',
              color:'#8F2E24',
              border:'1px solid #F5B7B1',
              borderRadius:8,
              padding:'10px 12px',
              marginBottom:14,
              fontSize:12
            }}>
              {decisionError}
            </div>
          )}

          {full.discount_status === 'pending' && (
            <div style={{
              background:'#FFF3DC',
              color:'#8B5E0A',
              border:'1px solid #F5C842',
              borderRadius:8,
              padding:'10px 12px',
              marginBottom:14,
              fontSize:12
            }}>
              This invoice contains a pending discount.
              It cannot be printed or sent until the
              discount is approved or rejected.
            </div>
          )}

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20,padding:16,background:"#F2EFE7",borderRadius:8}}>
            <div>
              <div style={{fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:"#8A8A8A",marginBottom:6}}>Bill To</div>
              <div style={{fontWeight:600,fontSize:15,color:"#3B2A21"}}>{full.customer_name}</div>
              {full.phone && <div style={{fontSize:12,color:"#8A8A8A",marginTop:2}}>{full.phone}</div>}
              {full.address && <div style={{fontSize:12,color:"#8A8A8A"}}>{full.address}</div>}
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:"#8A8A8A",marginBottom:6}}>Invoice Details</div>
              <div style={{fontSize:13,color:"#3B2A21",lineHeight:1.8}}>
                <div><span style={{color:"#8A8A8A"}}>Number: </span><strong>{full.invoice_number}</strong></div>
                <div><span style={{color:"#8A8A8A"}}>Date: </span>{full.date}</div>
                {full.due_date && <div><span style={{color:"#8A8A8A"}}>Due: </span>{full.due_date}</div>}
                <div style={{marginTop:6}}>
                  <span style={{display:"inline-flex",padding:"3px 10px",borderRadius:20,fontSize:10,fontWeight:600,textTransform:"uppercase",background:statusBg,color:statusColor}}>
                    {full.status && full.status.replace(/_/g," ")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <table style={{width:"100%",borderCollapse:"collapse",marginBottom:16}}>
            <thead>
              <tr style={{background:"#556B2F"}}>
                <th style={{padding:"9px 12px",textAlign:"left",fontSize:10,color:"#F2EFE7",textTransform:"uppercase",letterSpacing:".08em"}}>Description</th>
                <th style={{padding:"9px 12px",textAlign:"center",fontSize:10,color:"#F2EFE7",textTransform:"uppercase",letterSpacing:".08em",width:60}}>Qty</th>
                <th style={{padding:"9px 12px",textAlign:"right",fontSize:10,color:"#F2EFE7",textTransform:"uppercase",letterSpacing:".08em",width:120}}>Unit Price</th>
                <th style={{padding:"9px 12px",textAlign:"right",fontSize:10,color:"#F2EFE7",textTransform:"uppercase",letterSpacing:".08em",width:120}}>Total</th>
              </tr>
            </thead>
            <tbody>
              {(full.lines||[]).map((l,i) => (
                <tr key={l.id} style={{background:i%2===0?"#fff":"#F2EFE7"}}>
                  <td style={{padding:"9px 12px",borderBottom:"1px solid #EDE7D8",fontSize:13}}>{l.description}</td>
                  <td style={{padding:"9px 12px",borderBottom:"1px solid #EDE7D8",textAlign:"center",fontSize:13}}>{l.quantity}</td>
                  <td style={{padding:"9px 12px",borderBottom:"1px solid #EDE7D8",textAlign:"right",fontSize:14}}>{fmt(l.unit_price)}</td>
                  <td style={{padding:"9px 12px",borderBottom:"1px solid #EDE7D8",textAlign:"right",fontSize:14,fontWeight:600}}>{fmt(l.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:16}}>
            <div style={{width:280,background:"#F2EFE7",borderRadius:8,padding:14}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"4px 0",borderBottom:"1px solid #DDD4C0"}}>
                <span style={{color:"#8A8A8A"}}>Subtotal</span><span>{fmt(full.subtotal)}</span></div>
              {Number(full.refund_reduction_total || 0) > 0 && <div style={{display:'flex',justifyContent:'space-between',padding:'6px 0',fontWeight:700}}><span>Approved refund reductions</span><span>-{fmt(full.refund_reduction_total)}</span></div>}
              {parseFloat(full.discount_amount||0)>0 && (
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"4px 0",borderBottom:"1px solid #DDD4C0"}}>
                  <span style={{color:"#8A8A8A"}}>Discount</span>
                  <span style={{color:"#8F2E24"}}>-{fmt(full.discount_amount)}</span></div>
              )}
              <div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"4px 0",borderBottom:"1px solid #DDD4C0"}}>
                <span style={{color:"#8A8A8A"}}>80% Deposit</span>
                <span style={{fontWeight:600,color:"#8F2E24"}}>{fmt(parseFloat(full.total)*0.8)}</span></div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"4px 0",borderBottom:"1px solid #DDD4C0"}}>
                <span style={{color:"#8A8A8A"}}>Balance 20%</span><span>{fmt(parseFloat(full.total)*0.2)}</span></div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0 0"}}>
                <span style={{fontWeight:700,fontSize:14,color:"#3B2A21"}}>TOTAL</span>
                <span style={{fontFamily:"Cormorant Garamond,serif",fontSize:"1.4rem",fontWeight:700,color:"#556B2F"}}>{fmt(full.total)}</span>
              </div>
              {parseFloat(full.amount_paid||0)>0 && (
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"6px 0 0",borderTop:"1px solid #DDD4C0",marginTop:4}}>
                  <span style={{color:"#8A8A8A"}}>Amount Paid</span>
                  <span style={{color:"var(--paid)",fontWeight:600}}>{fmt(full.amount_paid)}</span></div>
              )}
              {parseFloat(full.balance_due||0)>0 && (
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"4px 0 0"}}>
                  <span style={{color:"#8A8A8A"}}>Balance Due</span>
                  <span style={{color:"#8F2E24",fontWeight:600}}>{fmt(full.balance_due)}</span></div>
              )}
            </div>
          </div>

          {paymentUrl && (
            <div style={{
              marginBottom:16,
              padding:14,
              background:'#EFF7F0',
              border:'1px solid #B8D4BD',
              borderRadius:8
            }}>
              <div style={{
                fontSize:10,
                fontWeight:700,
                letterSpacing:'.1em',
                textTransform:'uppercase',
                color:'#556B2F',
                marginBottom:8
              }}>
                Customer Payment Link
              </div>

              <div style={{
                fontSize:12,
                color:'#555',
                marginBottom:10
              }}>
                Pay securely through Paystack or use
                the bank-transfer option.
              </div>

              <div style={{
                padding:'8px 10px',
                marginBottom:10,
                background:'#fff',
                border:'1px solid #DDD4C0',
                borderRadius:6,
                fontSize:11,
                overflowWrap:'anywhere'
              }}>
                {paymentUrl}
              </div>

              <div style={{
                display:'flex',
                gap:8,
                flexWrap:'wrap'
              }}>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={copyingPaymentLink}
                  onClick={copyPaymentLink}
                >
                  {copyingPaymentLink
                    ? 'Copying...'
                    : 'Copy Payment Link'}
                </button>

                <a
                  className="btn btn-outline btn-sm"
                  href={paymentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{textDecoration:'none'}}
                >
                  Open Payment Page
                </a>
              </div>
            </div>
          )}

          {paymentLinkError && (
            <div
              className="alert alert-error mb-md"
              style={{fontSize:12}}
            >
              {paymentLinkError}
            </div>
          )}

          {(full.payments||[]).length > 0 && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:"#8A8A8A",marginBottom:8}}>Payment History</div>
              {full.payments.map(p => (
                <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",background:"#F2EFE7",borderRadius:6,marginBottom:4,fontSize:13}}>
                  <div>
                    <span style={{fontWeight:500}}>{p.payment_number}</span>
                    <span style={{color:"#8A8A8A",marginLeft:10}}>{p.date}</span>
                    <span style={{color:"#8A8A8A",marginLeft:10,textTransform:"capitalize"}}>{p.method}</span>
                    {p.reference && <span style={{color:"#8A8A8A",marginLeft:10}}>Ref: {p.reference}</span>}
                  </div>
                  <span style={{fontSize:14,fontWeight:600,color:"var(--paid)"}}>{fmt(p.amount)}</span>
                </div>
              ))}
            </div>
          )}
          {full.notes && (
            <div style={{padding:12,background:"#F2EFE7",borderRadius:8,fontSize:13,color:"#3B2A21"}}>
              <strong style={{display:"block",marginBottom:4,fontSize:10,letterSpacing:".1em",textTransform:"uppercase",color:"#8A8A8A"}}>Notes</strong>
              {full.notes}
            </div>
          )}

          {/* Refunds Section */}
          <div style={{marginTop:16,borderTop:'1px solid var(--border-light)',paddingTop:16}}>
            <InvoiceRefunds invoice={full} onUpdated={async () => { await refreshInvoice(); if (onRefundUpdated) onRefundUpdated(); }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── PAYMENT MODAL ─────────────────────────────────────────
function PaymentModal({ invoice, onClose, onSaved }) {
  const paymentRequestId =
    React.useRef(
      window.crypto.randomUUID()
    );

  const submissionLock =
    React.useRef(false);

  const [form, setForm] = React.useState({
    date: new Date().toISOString().split("T")[0],
    amount: invoice.balance_due,
    method: "transfer",
    reference: "",
    notes: "",
    bank_account_id: ""
  });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [bankAccounts, setBankAccounts] = React.useState([]);

  React.useEffect(() => {
    get('/bank-accounts').then(d => {
      const accounts = d.data||[];
      setBankAccounts(accounts);
      // Explicitly choose the receiving account for each payment.
    });
  }, []);

  const submit = async () => {
    if (submissionLock.current) {
      return;
    }

    setError('');

    const paymentAmount =
      Number(form.amount);

    if (
      !Number.isFinite(
        paymentAmount
      ) ||
      paymentAmount <= 0
    ) {
      setError(
        'Enter a valid payment amount greater than zero'
      );
      return;
    }

    if (
      paymentAmount >
      Number(
        invoice.balance_due || 0
      )
    ) {
      setError(
        'Payment exceeds balance due'
      );
      return;
    }

    if(!form.bank_account_id){setError('Select the account that received the payment');return;}
    submissionLock.current = true;
    setSaving(true);

    try {
      await post(
        "/invoices/" +
          invoice.id +
          "/payments",
        {
          ...form,
          request_id:
            paymentRequestId.current
        }
      );

      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      submissionLock.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay"><div className="modal">
      <div className="modal-hdr">
        <span className="modal-title">Record Payment</span>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
      </div>
      <div className="modal-body">
        {error && <div className="alert alert-error mb-md">{error}</div>}
        <div style={{background:"#F2EFE7",padding:12,borderRadius:8,marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <span style={{color:"#8A8A8A",fontSize:13}}>Customer</span>
            <span style={{fontWeight:500}}>{invoice.customer_name}</span>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
            <span style={{color:"#8A8A8A",fontSize:13}}>Balance Due</span>
            <span style={{fontFamily:"Cormorant Garamond,serif",fontSize:"1.1rem",color:"var(--unpaid)",fontWeight:600}}>
              {fmt(invoice.balance_due)}
            </span>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Date *</label>
            <div className="form-group" style={{marginBottom:12}}>
              <label className="form-label">Document Type</label>
              <select className="form-control" value={form.invoice_type}
                onChange={e=>setForm({...form,invoice_type:e.target.value})}>
                <option value="invoice">Invoice</option>
                <option value="proforma">Proforma Invoice</option>
              </select>
            </div>
            <input type="date" className="form-control" value={form.date}
              onChange={e => setForm({...form,date:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Amount *</label>
            <input type="number" className="form-control" value={form.amount}
              onChange={e => setForm({...form,amount:e.target.value})} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Method *</label>
            <select className="form-control" value={form.method}
              onChange={e => setForm({...form,method:e.target.value,bank_account_id:''})}>
              <option value="transfer">Bank Transfer</option>
              <option value="cash">Cash</option>
              <option value="pos">POS</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Reference</label>
            <input className="form-control" value={form.reference}
              onChange={e => setForm({...form,reference:e.target.value})} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Received Into *</label>
          <select className="form-control" value={form.bank_account_id}
            onChange={e => setForm({...form,bank_account_id:e.target.value})}>
            <option value="">Select account...</option>
            {bankAccounts.filter(a=>a.type===(form.method==='cash'?'cash':'bank')).map(a => (
              <option key={a.id} value={a.id}>{a.name} ({a.bank_name})</option>
            ))}
          </select>
        </div>
      </div>
      <div className="modal-ftr">
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-gold" onClick={submit} disabled={saving}>
          {saving ? "Recording..." : "Record Payment"}
        </button>
      </div>
    </div></div>
  );
}

// ── PRINT / PDF MODAL ─────────────────────────────────────
const escapeInvoicePrintHtml = value =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const safeInvoiceSignatureDataUrl = value => {
  const candidate =
    String(value || '').trim();

  if (
    !candidate ||
    candidate.length > 3 * 1024 * 1024 ||
    !/^data:image\/png;base64,[A-Za-z0-9+/=]+$/.test(
      candidate
    )
  ) {
    return '';
  }

  return candidate;
};

const invoiceSignatureDate = value => {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString(
    'en-NG',
    {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }
  );
};

function PrintInvoiceModal({ invoice, onClose }) {
  const [full, setFull] = React.useState(null);
  const [settings, setSettings] = React.useState(null);
  const [paymentUrl, setPaymentUrl] =
    React.useState('');

  React.useEffect(() => {
    Promise.all([
      get('/invoices/' + invoice.id),
      get('/invoice-settings')
    ]).then(([inv, cfg]) => {
      setFull(inv);
      setSettings(cfg);

      const eligible =
        Number(inv.balance_due || 0) > 0 &&
        inv.status !== 'paid' &&
        inv.status !== 'cancelled' &&
        inv.discount_status !== 'pending';

      if (!eligible) {
        setPaymentUrl('');
        return;
      }

      post(
        '/invoice-payment-links/invoice/' +
          inv.id,
        {}
      )
        .then(response =>
          setPaymentUrl(
            response?.data?.payment_url || ''
          )
        )
        .catch(error => {
          const message =
            error?.message || '';

          if (
            !/not currently available|not configured|disabled/i
              .test(message)
          ) {
            console.error(
              'Invoice payment link failed:',
              message
            );
          }
        });
    });
  }, [invoice.id]);

  const doPrint = () => {
    if (
      full?.discount_status === 'pending'
    ) {
      window.alert(
        'This invoice has a pending discount. Approve or reject the discount before printing or sending the final invoice.'
      );
      return;
    }

    const w = window.open('', '_blank');
    const f = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');
    const s = settings || {};

    const signatureUrl =
      safeInvoiceSignatureDataUrl(
        full?.company_signature_data_url
      );

    const signatureName =
      escapeInvoicePrintHtml(
        full?.company_signer_name ||
        'Doris Gideon-Ayantola'
      );

    const signatureTitle =
      escapeInvoicePrintHtml(
        full?.company_signer_title ||
        'Managing Director'
      );

    const signedDate =
      escapeInvoicePrintHtml(
        invoiceSignatureDate(
          full?.company_signed_at
        )
      );
    const total    = parseFloat(full?.total||0);
    const dep80    = total * 0.8;
    const bal20    = total * 0.2;
    const amtPaid  = parseFloat(full?.amount_paid||0);
    const balDue   = parseFloat(full?.balance_due||0);
    const subtotal = parseFloat(full?.subtotal||0);
    const discount = parseFloat(full?.discount_amount||0);
    const safePaymentUrl =
      escapeInvoicePrintHtml(
        paymentUrl
      );
    const sBg  = full?.status==='paid'?'#E8F5EB':full?.status==='part_paid'?'#FFF3DC':'#FDEAEA';
    const sCol = full?.status==='paid'?'#2E6B3A':full?.status==='part_paid'?'#8B5E0A':'#8F2E24';

    const logoHtml = s.logo_url
      ? `<img src="${s.logo_url}" style="width:80px;height:80px;object-fit:contain;margin-right:16px;flex-shrink:0" onerror="this.style.display='none'">`
      : '';

    const lineRows = (full?.lines||[]).map((l,i) => `
      <tr style="background:${i%2===0?'#fff':'#F2EFE7'}">
        <td style="padding:10px 12px;border-bottom:1px solid #EDE7D8;font-size:12.5px">${l.description||''}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #EDE7D8;font-size:12px;color:#555">${l.size||''}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #EDE7D8;text-align:center;font-size:12.5px">${Number(l.quantity).toLocaleString()}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #EDE7D8;text-align:right;font-size:13px">${f(l.unit_price)}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #EDE7D8;text-align:right;font-size:13px;font-weight:700">${f(l.line_total)}</td>
      </tr>`).join('');

    const terms = [s.term_1,s.term_2,s.term_3,s.term_4,s.term_5]
      .filter(Boolean)
      .map((t,i) => `<li style="margin-bottom:4px">${t}</li>`).join('');

    w.document.write(`<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<title>Invoice ${full?.invoice_number}</title>
<link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet">
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', sans-serif; background: #fff; color: #1A1A1A; }
  @media print {
    body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .no-print { display: none; }
    @page { margin: 0.5cm; size: A4; }
  }
</style>
</head>
<body>
<div style="max-width:740px;margin:0 auto;background:#fff">

  <!-- HEADER -->
  <div style="background:#556B2F;padding:22px 28px;display:flex;align-items:center;justify-content:space-between">
    <div style="display:flex;align-items:center">
      ${logoHtml}
      <div>
        <div style="font-family:'Cormorant Garamond',serif;font-size:1.55rem;font-weight:700;color:#A8893D;letter-spacing:.02em">${s.company_name||'BLOANS CREATIONS LTD'}</div>
        <div style="font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;color:rgba(242,239,231,.55);margin-top:3px">${s.company_tagline||'Premium Jute & Luxury Gift Packaging'}</div>
        <div style="font-size:13px;color:rgba(242,239,231,.5);margin-top:6px;line-height:1.7">
          ${s.company_address||''}<br>
          ☎ ${s.company_phone||''} &nbsp;|&nbsp; Dispatch: ${s.company_dispatch||''} &nbsp;|&nbsp; Complaints: ${s.company_complaints||''}<br>
          ${s.company_website||''}
        </div>
      </div>
    </div>
    <div style="text-align:right;flex-shrink:0;margin-left:20px">
      <div style="font-family:'Cormorant Garamond',serif;font-size:1.4rem;font-weight:600;color:#A8893D;letter-spacing:.04em">PROFORMA INVOICE</div>
      <div style="margin-top:8px;font-size:12.5px;color:rgba(242,239,231,.8);line-height:1.9">
        <div><span style="color:rgba(242,239,231,.5)">Invoice No: </span><strong style="color:#F2EFE7">${full?.invoice_number}</strong></div>
        <div><span style="color:rgba(242,239,231,.5)">Invoice Date: </span>${full?.date}</div>
        <div><span style="color:rgba(242,239,231,.5)">Valid For: </span>7 days</div>
      </div>
    </div>
  </div>

  <!-- BILL TO -->
  <div style="background:#F2EFE7;padding:16px 28px;border-bottom:2px solid #DDD4C0">
    <div style="font-size:13px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#8A8A8A;margin-bottom:8px">BILL TO</div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
      <div>
        <div style="font-weight:700;font-size:14px;color:#3B2A21">${full?.customer_name}</div>
        ${full?.address ? `<div style="font-size:13px;color:#555;margin-top:3px;line-height:1.6">${full?.address}</div>` : ''}
      </div>
      <div style="font-size:13px;color:#555;line-height:1.9">
        ${full?.phone ? `<div><span style="color:#8A8A8A">Phone: </span>${full?.phone}</div>` : ''}
        ${full?.email ? `<div><span style="color:#8A8A8A">Email: </span>${full?.email}</div>` : ''}
      </div>
    </div>
  </div>

  <!-- LINE ITEMS -->
  <table style="width:100%;border-collapse:collapse">
    <thead>
      <tr style="background:#556B2F">
        <th style="padding:10px 12px;text-align:left;font-size:9.5px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:#F2EFE7">#&nbsp;&nbsp;Description</th>
        <th style="padding:10px 12px;text-align:left;font-size:9.5px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:#F2EFE7;width:100px">Size</th>
        <th style="padding:10px 12px;text-align:center;font-size:9.5px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:#F2EFE7;width:70px">Qty</th>
        <th style="padding:10px 12px;text-align:right;font-size:9.5px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:#F2EFE7;width:110px">Unit Price</th>
        <th style="padding:10px 12px;text-align:right;font-size:9.5px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:#F2EFE7;width:110px">Amount</th>
      </tr>
    </thead>
    <tbody>${lineRows}</tbody>
  </table>

  <!-- TOTALS + PAYMENT -->
  <div style="display:grid;grid-template-columns:1fr 1fr;border-top:2px solid #DDD4C0">

    <!-- Payment Details -->
    <div style="padding:18px 24px;border-right:1px solid #EDE7D8;background:#F2EFE7">
      <div style="font-size:13px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#8A8A8A;margin-bottom:10px">PAYMENT DETAILS</div>
      <div style="font-size:12.5px;color:#1A1A1A;line-height:2.1">
        <div><span style="color:#8A8A8A">Bank: </span><strong>${s.bank_name||'Moniepoint MFB'}</strong></div>
        <div><span style="color:#8A8A8A">Account No: </span><strong style="font-size:14px;letter-spacing:.04em">${s.bank_account_no||'5210916081'}</strong></div>
        <div><span style="color:#8A8A8A">Account Name: </span><strong>${s.bank_account_name||'Bloans Creations Ltd'}</strong></div>
      </div>
    </div>

    <!-- Totals -->
    <div style="padding:18px 24px;background:#F2EFE7">
      <div style="font-size:13px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#8A8A8A;margin-bottom:10px">&nbsp;</div>
      <table style="width:100%;border-collapse:collapse;font-size:12.5px">
        <tr>
          <td style="padding:5px 0;border-bottom:1px solid #DDD4C0;color:#8A8A8A">Subtotal</td>
          <td style="padding:5px 0;border-bottom:1px solid #DDD4C0;text-align:right">${f(subtotal)}</td>
        </tr>
        ${discount>0?`<tr>
          <td style="padding:5px 0;border-bottom:1px solid #DDD4C0;color:#8A8A8A">Discount</td>
          <td style="padding:5px 0;border-bottom:1px solid #DDD4C0;text-align:right;color:#8F2E24">-${f(discount)}</td>
        </tr>`:''}
        ${Number(full?.refund_reduction_total||0)>0?`<tr><td style="padding:5px 0;font-weight:700">Approved refund reductions</td><td style="padding:5px 0;text-align:right;font-weight:700">-${f(full.refund_reduction_total)}</td></tr>`:''}
        <tr>
          <td style="padding:5px 0;border-bottom:1px solid #DDD4C0;color:#8A8A8A"><strong>80% Deposit Due</strong></td>
          <td style="padding:5px 0;border-bottom:1px solid #DDD4C0;text-align:right;font-weight:700;color:#8F2E24"><strong>${f(dep80)}</strong></td>
        </tr>
        <tr>
          <td style="padding:5px 0;border-bottom:1px solid #DDD4C0;color:#8A8A8A">Balance (20%)</td>
          <td style="padding:5px 0;border-bottom:1px solid #DDD4C0;text-align:right">${f(bal20)}</td>
        </tr>
        <tr>
          <td style="padding:8px 0 4px;font-weight:700;font-size:14px;color:#3B2A21">TOTAL</td>
          <td style="padding:8px 0 4px;text-align:right;font-family:'Cormorant Garamond',serif;font-size:1.5rem;font-weight:700;color:#556B2F">${f(total)}</td>
        </tr>
        ${amtPaid>0?`<tr>
          <td style="padding:4px 0;border-top:1px solid #DDD4C0;color:#8A8A8A">Amount Paid</td>
          <td style="padding:4px 0;border-top:1px solid #DDD4C0;text-align:right;color:#2E6B3A;font-weight:600">${f(amtPaid)}</td>
        </tr>`:''}
        ${balDue>0?`<tr>
          <td style="padding:4px 0;color:#8A8A8A">Balance Due</td>
          <td style="padding:4px 0;text-align:right;color:#8F2E24;font-weight:700">${f(balDue)}</td>
        </tr>`:''}
      </table>
    </div>
  </div>

  ${safePaymentUrl && balDue > 0 ? `
    <div style="padding:14px 28px;background:#EFF7F0;border-top:1px solid #B8D4BD;border-bottom:1px solid #B8D4BD">
      <div style="font-size:12px;font-weight:700;color:#556B2F;margin-bottom:6px">
        PAY THIS INVOICE
      </div>

      <div style="font-size:11.5px;color:#3B2A21">
        Pay securely online or use the bank-transfer option:
      </div>

      <a
        href="${safePaymentUrl}"
        style="display:inline-block;margin-top:6px;color:#2E6B3A;font-size:11px;font-weight:600;overflow-wrap:anywhere"
      >
        ${safePaymentUrl}
      </a>

      <div style="font-size:10.5px;color:#777;margin-top:6px">
        Direct bank transfer remains available using the
        account details above. Use
        ${escapeInvoicePrintHtml(
          full?.invoice_number || ''
        )} as the payment reference.
      </div>
    </div>
  ` : ''}

  <!-- TERMS -->
  <div style="padding:16px 28px;background:#fff;border-top:1px solid #DDD4C0;display:grid;grid-template-columns:1fr 1fr;gap:24px">
    <div>
      <div style="font-size:13px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#8A8A8A;margin-bottom:8px">TERMS & CONDITIONS</div>
      <ol style="font-size:13px;color:#4A4A4A;line-height:1.8;padding-left:16px">${terms}</ol>
    </div>
    <div style="display:flex;align-items:center;justify-content:center">
      <div style="text-align:center;padding:12px;border:1px dashed #DDD4C0;border-radius:8px;width:100%;min-height:128px;display:flex;flex-direction:column;align-items:center;justify-content:center">
        ${signatureUrl ? `
          <div style="font-size:11px;color:#777;margin-bottom:3px">
            Electronically authorised by
          </div>
          <img
            src="${signatureUrl}"
            alt="Managing Director signature"
            style="display:block;width:190px;max-width:100%;height:70px;object-fit:contain;object-position:center;margin:0 auto 3px"
          >
          <div style="font-size:12px;font-weight:700;color:#3B2A21">
            ${signatureName}
          </div>
          <div style="font-size:10px;color:#777">
            ${signatureTitle}
          </div>
          ${signedDate ? `
            <div style="font-size:9px;color:#888;margin-top:2px">
              ${signedDate}
            </div>
          ` : ''}
        ` : `
          <div style="font-size:12px;font-weight:600;color:#8A8A8A">
            Unsigned historical invoice
          </div>
          <div style="font-size:10px;color:#999;margin-top:5px">
            Issued before electronic signatures were activated
          </div>
        `}
      </div>
    </div>
  </div>

  <!-- FOOTER -->
  <div style="background:#556B2F;padding:12px 28px;text-align:center">
    <div style="font-size:11.5px;color:rgba(242,239,231,.65);font-style:italic">
      ${s.footer_text||'Thank you for choosing Bloans Creations Ltd'}
    </div>
    <div style="font-size:10.5px;color:rgba(242,239,231,.45);margin-top:4px">
      ${s.company_website||'bloanshandbags.com'} &nbsp;|&nbsp; ${s.company_address||''}
    </div>
  </div>

</div>

</body></html>`);
    w.document.close();
  };

  if (!full || !settings) return (
    <div className="modal-overlay">
      <div className="modal" style={{textAlign:'center',padding:40}}>
        <div className="spinner"></div>
        <p className="text-muted" style={{marginTop:12}}>Loading invoice...</p>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:460}}>
        <div className="modal-hdr">
          <span className="modal-title">Download PDF — {full.invoice_number}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          <div style={{background:'#F2EFE7',borderRadius:8,padding:16,marginBottom:16}}>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:6}}>
              <span style={{color:'#8A8A8A'}}>Customer</span>
              <strong>{full.customer_name}</strong>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:6}}>
              <span style={{color:'#8A8A8A'}}>Invoice</span>
              <span>{full.invoice_number}</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:6}}>
              <span style={{color:'#8A8A8A'}}>Total</span>
              <strong style={{color:'#556B2F',fontFamily:'Cormorant Garamond,serif',fontSize:16}}>
                {'\u20a6'}{Number(full.total||0).toLocaleString('en-NG')}
              </strong>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13}}>
              <span style={{color:'#8A8A8A'}}>Status</span>
              <span style={{fontWeight:600,textTransform:'capitalize',
                color:full.status==='paid'?'#2E6B3A':full.status==='part_paid'?'#8B5E0A':'#8F2E24'}}>
                {full.status?.replace(/_/g,' ')}
              </span>
            </div>
          </div>

          {settings.logo_url && (
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,padding:'10px 14px',background:'#F2EFE7',borderRadius:8}}>
              <img src={settings.logo_url} style={{width:40,height:40,objectFit:'contain'}} alt="logo" />
              <span style={{fontSize:12,color:'#555'}}>Logo loaded from Invoice Settings</span>
            </div>
          )}
          {!settings.logo_url && (
            <div className="alert alert-warn mb-md" style={{fontSize:12}}>
              No logo set. Go to <strong>Invoice Settings</strong> to add your company logo.
            </div>
          )}

          <button className="btn btn-primary"
            style={{width:'100%',justifyContent:'center',fontSize:15,padding:14,marginBottom:8}}
            onClick={doPrint}>
            🖨 Open Print Dialog / Save as PDF
          </button>
          <p className="text-muted text-xs" style={{textAlign:'center'}}>
            In the print dialog — select <strong>Save as PDF</strong> as the destination
          </p>
        </div>
      </div>
    </div>
  );
}

function DeleteInvoiceModal({ invoice, onClose, onDeleted }) {
  const [reason, setReason] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const submit = async () => {
    if (!reason.trim()) { setError("Please provide a reason for deletion"); return; }
    setSaving(true);
    try {
      await del("/invoices/"+invoice.id, { reason });
      onDeleted();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:440}}>
        <div className="modal-hdr">
          <span className="modal-title">Delete Invoice</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          <div className="alert alert-error mb-md">
            You are about to delete <strong>{invoice.invoice_number}</strong> for <strong>{invoice.customer_name}</strong> — {fmt(invoice.total)}.
            This cannot be undone.
          </div>
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-group">
            <label className="form-label">Reason for deletion *</label>
            <textarea className="form-control" rows={3}
              placeholder="e.g. Duplicate entry, customer cancelled order..."
              value={reason} onChange={e => setReason(e.target.value)} />
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={submit} disabled={saving}>
            {saving ? "Deleting..." : "Yes, Delete Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ThermalReceiptModal({ invoice, onClose }) {
  const [full, setFull] = React.useState(null);
  const [printerWidth, setPrinterWidth] = React.useState('80mm');
  React.useEffect(() => {
    get('/invoices/' + invoice.id).then(setFull);
  }, [invoice.id]);

  const doPrint = () => {
    const w = window.open('', '_blank');
    const f = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');
    const inv = full || invoice;
    const itemLines = (inv.lines||[]).map(l =>
      `<div style="margin-bottom:8px;padding-bottom:6px;border-bottom:1px solid #000;">` +
      `<div style="font-size:11pt;font-weight:900;">${l.description||''} ${l.size?'('+l.size+')':''}</div>` +
      `<div style="font-size:10.5pt;font-weight:700;">Qty: ${l.quantity} | Unit: ${f(l.unit_price)} | <strong>${f(l.line_total)}</strong></div>` +
      `</div>`
    ).join('');
    const invTotal = parseFloat(inv.total||inv.total_amount||0);
    const status = parseFloat(inv.balance_due||0)<=0?'PAID':parseFloat(inv.amount_paid||0)>0?'PART PAYMENT':'PAYMENT PENDING';
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Receipt ${inv.invoice_number}</title>
<style>
* { box-sizing:border-box; margin:0; padding:0; }
body { font-family:Arial,Helvetica,sans-serif; width:74mm; max-width:74mm; margin:0 auto; padding:3mm; font-size:10.5pt; font-weight:700; line-height:1.3; color:#000000; background:#ffffff; }
@media print { @page { size:80mm auto; margin:3mm; } * { color:#000000 !important; -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; } body { margin:0; padding:0; } }
.cn { font-size:17pt; font-weight:900; text-align:center; display:block; }
.dt { font-size:14pt; font-weight:900; text-align:center; display:block; margin:4px 0; }
.st { font-size:11pt; font-weight:900; text-transform:uppercase; margin:8px 0 3px; border-bottom:2px solid #000; padding-bottom:2px; display:block; }
.val { font-size:11pt; font-weight:900; display:block; word-wrap:break-word; }
.row { display:flex; justify-content:space-between; font-size:10.5pt; font-weight:700; margin:3px 0; }
.gt { font-size:14pt; font-weight:900; }
.div { border-top:2px solid #000; margin:6px 0; }
.ctr { text-align:center; display:block; }
.ftr { font-size:9.5pt; font-weight:700; text-align:center; display:block; }
.badge { font-size:12pt; font-weight:900; text-align:center; border:2px solid #000; padding:4px; margin:6px 0; display:block; }
</style>
<script>window.onload=function(){window.print();}</script>
</head><body>
<span class="cn">BLOANS HANDBAGS</span>
<span class="ctr" style="font-size:10pt;">No. 3, S. Ade-Ojo Estate, Sawmill, Old Ife Road, Ibadan</span>
<div class="div"></div>
<span class="dt">${inv.is_proforma?'PROFORMA INVOICE':'INVOICE'}</span>
<div class="div"></div>
<span class="st">Invoice Details</span>
<div class="row"><span>Invoice No:</span><span style="font-weight:900;">${inv.invoice_number||''}</span></div>
<div class="row"><span>Date:</span><span>${inv.date||''}</span></div>
${inv.order_number?`<div class="row"><span>Order No:</span><span>${inv.order_number}</span></div>`:''}
<span class="st">Customer</span>
<span class="val">${inv.customer_name||''}</span>
${inv.customer_phone?`<span class="val">${inv.customer_phone}</span>`:''}
${inv.customer_email?`<span style="font-size:10pt;font-weight:700;">${inv.customer_email}</span>`:''}
<span class="st">Items</span>
${itemLines}
<span class="st">Payment Summary</span>
${parseFloat(inv.discount_amount||0)>0?`<div class="row"><span>Subtotal:</span><span>${f(inv.subtotal)}</span></div><div class="row"><span>Discount:</span><span>-${f(inv.discount_amount)}</span></div>`:''}
${Number(inv.refund_reduction_total||0)>0?`<div class="row"><span>Approved refund reductions:</span><span>-${f(inv.refund_reduction_total)}</span></div>`:''}
${parseFloat(inv.delivery_fee||0)>0?`<div class="row"><span>Delivery Fee:</span><span>${f(inv.delivery_fee)}</span></div>`:''}
${parseFloat(inv.other_charges||0)>0?`<div class="row"><span>Other Charges:</span><span>${f(inv.other_charges)}</span></div>`:''}
<div class="div"></div>
<div class="row gt"><span>GRAND TOTAL:</span><span>${f(invTotal)}</span></div>
<div class="div"></div>
${parseFloat(inv.amount_paid||0)>0?`<div class="row"><span>Amount Paid:</span><span>${f(inv.amount_paid)}</span></div>`:''}
${parseFloat(inv.balance_due||0)>0?`<div class="row" style="font-size:12pt;font-weight:900;"><span>Balance Due:</span><span>${f(inv.balance_due)}</span></div>`:''}
<span class="badge">${status}</span>
${inv.payment_method?`<div class="row"><span>Payment Method:</span><span>${inv.payment_method}</span></div>`:''}
${inv.sales_rep_name?`<div class="row"><span>Sales Rep:</span><span>${inv.sales_rep_name}</span></div>`:''}
${inv.notes?`<span style="font-size:10pt;font-weight:700;margin-top:6px;display:block;">Note: ${inv.notes}</span>`:''}
<div class="div"></div>
<span class="ftr">Thank you for choosing Bloans Handbags.</span>
<span class="ftr">www.bloanshandbags.com</span>
</body></html>`;
    w.document.write(html);
    w.document.close();
  };
  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:340}}>
        <div className="modal-hdr">
          <span className="modal-title">🖨️ Thermal Receipt</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Printer Width</label>
            <select className="form-control" value={printerWidth} onChange={e=>setPrinterWidth(e.target.value)}>
              <option value="80mm">80mm (Standard)</option>
              <option value="58mm">58mm (Narrow)</option>
            </select>
          </div>
          <div style={{background:'#E8F5EB',borderRadius:8,padding:10,fontSize:12,color:'#2E6B3A'}}>
            A print preview will open in a new tab. Use your browser print dialog to send to your thermal printer.
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={doPrint} disabled={!full}>
            {full ? '🖨️ Open Print Preview' : 'Loading...'}
          </button>
        </div>
      </div>
    </div>
  );
}
